import { useParams, Link, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowLeft, Calendar, Newspaper } from 'lucide-react'
import { SEO } from '@/components/SEO'
import { newsItems } from './newsData'
import type { SupportedLanguage } from '@/i18n'

export function NewsDetailPage() {
  const { t, i18n } = useTranslation('common')
  const { newsId } = useParams<{ newsId: string }>()
  const lang = i18n.language as SupportedLanguage

  const news = newsItems.find((item) => item.id === newsId)

  if (!news) {
    return <Navigate to={`/${lang}/news`} replace />
  }

  const newsListPath = `/${lang}/news`

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const locale = lang === 'it' ? 'it-IT' : 'en-US'
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const relatedNews = newsItems
    .filter((item) => item.id !== news.id)
    .slice(0, 3)

  return (
    <>
      <SEO
        title={`${t(news.titleKey)} | ${t('nav.news')}`}
        description={t(news.excerptKey)}
        type="article"
        image={news.image}
      />

      <main id="main-content">
        {/* Hero Image */}
        <section className="relative h-[50vh] min-h-[320px] max-h-[600px] overflow-hidden">
          <img
            src={news.image}
            alt={t(news.titleKey)}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.src = '/images/news_iniziative.png'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/80 via-[#4E2118]/30 to-transparent" />

          {/* Back Button */}
          <div className="absolute top-6 left-6 z-10">
            <Link
              to={newsListPath}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur-sm text-[#4E2118] text-sm font-semibold rounded-full shadow-lg hover:bg-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('news.detail.backToNews')}
            </Link>
          </div>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0">
            <div className="container-fiorucci pb-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Category & Date */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#DF631E] text-white text-xs font-bold rounded-full shadow-md">
                    <Newspaper className="w-3.5 h-3.5" />
                    {news.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-white/90 text-sm">
                    <Calendar className="w-4 h-4" />
                    <time dateTime={news.date}>{formatDate(news.date)}</time>
                  </span>
                </div>

                <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-4xl">
                  {t(news.titleKey)}
                </h1>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="section-padding bg-[#FDF8F3]">
          <div className="container-fiorucci">
            <div className="max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-white rounded-3xl p-8 md:p-12 shadow-xl"
              >
                <p className="text-body-lg text-[#4E2118]/80 leading-relaxed">
                  {t(news.excerptKey)}
                </p>

                <div className="mt-10 pt-8 border-t border-[#F3E4D6]">
                  <Link
                    to={newsListPath}
                    className="inline-flex items-center gap-2 text-[#DF631E] font-semibold hover:gap-3 transition-all"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t('news.detail.backToNews')}
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Related News */}
        {relatedNews.length > 0 && (
          <section className="section-padding bg-white">
            <div className="container-fiorucci">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-10"
              >
                <h2 className="heading-section text-[#4E2118] text-center">
                  {t('news.detail.relatedNews')}
                </h2>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedNews.map((item, index) => (
                  <motion.article
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group bg-[#FDF8F3] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Link
                      to={`${newsListPath}/${item.id}`}
                      className="block"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={item.image}
                          alt={t(item.titleKey)}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.src = '/images/news_iniziative.png'
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/60 via-transparent to-transparent" />
                        <div className="absolute top-4 left-4 px-3 py-1 bg-white/95 backdrop-blur-sm text-[#4E2118] text-xs font-bold rounded-full shadow-sm">
                          {item.category}
                        </div>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 text-[#4E2118]/60 text-sm mb-2">
                          <Calendar className="w-4 h-4 text-[#DF631E]" />
                          <time dateTime={item.date}>{formatDate(item.date)}</time>
                        </div>
                        <h3 className="font-serif text-lg font-bold text-[#4E2118] group-hover:text-[#DF631E] transition-colors line-clamp-2">
                          {t(item.titleKey)}
                        </h3>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  )
}
